/**
 * ストリートビュー机上調査 結果地図のクラスタ・ドーナツ表示 (docs/25、Increment 3 追加)。
 *
 * `supercluster`（MapLibre GL JS 自体が内部利用する geospatial clustering ライブラリ、
 * apps/admin では既存の MapLibre 方針を維持するため Google Maps clustering は使わない）で
 * 投稿を空間集約し、クラスタ内の**全投稿の検出要素 (classifications 全件、`resolvePostRoleCounts`)**
 * を role_type 別に合算する (`map`/`reduce`)。地図中央のラベルはクラスタ内の地点数
 * (`point_count`) のまま据え置き、ドーナツの内訳だけが検出要素の合計比率になる —
 * 個別地点ピンのドーナツ表示 (`resolvePostRoleCounts`) と同じ集計単位に揃えたもの。
 * 地区が role_type 軸を定義していない場合は全投稿が空集計になり、呼出側は従来の
 * 単色クラスタ (件数のみ) にフォールバックできる。
 */
import Supercluster from 'supercluster';
import type { StreetSurveyPost } from './useStreetSurveyPosts';
import { safeCssColor } from '@citizen-walk/shared-ui';
import { resolveChipColor } from '../posts-moderation';
import {
  categoryRowKey,
  resolvePostCategoryCounts,
  type CategoryAxisResolution,
} from './streetSurveyPinDisplay';

export interface ClusterPointProps {
  postId: string;
  counts: Record<string, number>;
}

export interface ClusterAggProps {
  counts: Record<string, number>;
}

export type ClusterOrPointFeature =
  | Supercluster.ClusterFeature<ClusterAggProps>
  | Supercluster.PointFeature<ClusterPointProps>;

/**
 * `ClusterOrPointFeature` がクラスタか判定する type guard。`f.properties.cluster in` の
 * ような nested プロパティチェックだけでは TS の discriminated union 絞り込みが `f` 自体には
 * 伝播しないため、専用の type predicate 関数として切り出す。
 */
export function isClusterFeature(
  f: ClusterOrPointFeature,
): f is Supercluster.ClusterFeature<ClusterAggProps> {
  return 'cluster' in f.properties && f.properties.cluster === true;
}

/** クラスタ半径 (px)。ズームごとの粒度は supercluster の既定 (整数ズーム単位) に委ねる。 */
const CLUSTER_RADIUS_PX = 60;
const CLUSTER_MAX_ZOOM = 17;

/** posts から supercluster index を構築する (pure、呼出のたびに再構築する軽量な操作)。 */
export function buildClusterIndex(
  posts: StreetSurveyPost[],
  legendAxis: CategoryAxisResolution | undefined,
): Supercluster<ClusterPointProps, ClusterAggProps> {
  const index = new Supercluster<ClusterPointProps, ClusterAggProps>({
    radius: CLUSTER_RADIUS_PX,
    maxZoom: CLUSTER_MAX_ZOOM,
    map: (props) => ({ counts: { ...props.counts } }),
    reduce: (acc, props) => {
      for (const [k, v] of Object.entries(props.counts)) {
        acc.counts[k] = (acc.counts[k] ?? 0) + v;
      }
    },
  });
  const points: Supercluster.PointFeature<ClusterPointProps>[] = posts
    .filter((p) => !(p.location.latitude === 0 && p.location.longitude === 0))
    .map((p) => ({
      type: 'Feature',
      properties: {
        postId: p.id,
        counts: legendAxis ? resolvePostCategoryCounts(p, legendAxis) : {},
      },
      geometry: { type: 'Point', coordinates: [p.location.longitude, p.location.latitude] },
    }));
  index.load(points);
  return index;
}

export interface DonutSegment {
  cd: string;
  color: string;
  count: number;
}

/** クラスタの counts を軸マスタの行順 (安定ソート) で並べ、色を解決する。 */
export function resolveDonutSegments(
  counts: Record<string, number>,
  legendAxis: CategoryAxisResolution,
): DonutSegment[] {
  const segments: DonutSegment[] = [];
  for (const row of legendAxis.master?.rows ?? []) {
    const key = categoryRowKey(row);
    const count = counts[key];
    if (count) {
      segments.push({
        cd: key,
        color: resolveChipColor(row, legendAxis.master ?? undefined),
        count,
      });
    }
  }
  return segments;
}

/**
 * ドーナツチャートの SVG マークアップを生成する (pure、stroke-dasharray方式)。
 * segments が空 (role_type 未設定地区) の場合は単色の円のみを返す。
 */
export function buildDonutSvgMarkup(
  segments: DonutSegment[],
  size: number,
  strokeWidth: number,
  fallbackColor = '#94A3B8',
): string {
  // 色はマスタ由来で innerHTML に入る。属性値を壊せる文字列は既定色に置き換える (docs/13 §7)。
  const safeFallback = safeCssColor(fallbackColor, '#94A3B8');
  const r = (size - strokeWidth) / 2;
  const cx = size / 2;
  const cy = size / 2;
  const circumference = 2 * Math.PI * r;
  const total = segments.reduce((s, seg) => s + seg.count, 0);

  if (total === 0) {
    return `<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg"><circle cx="${cx}" cy="${cy}" r="${r}" fill="none" stroke="${safeFallback}" stroke-width="${strokeWidth}" /><circle cx="${cx}" cy="${cy}" r="${r - strokeWidth / 2 - 1}" fill="white" /></svg>`;
  }

  let offset = 0;
  const arcs = segments
    .map((seg) => {
      const dash = (seg.count / total) * circumference;
      const gap = circumference - dash;
      const markup = `<circle cx="${cx}" cy="${cy}" r="${r}" fill="none" stroke="${safeCssColor(seg.color, safeFallback)}" stroke-width="${strokeWidth}" stroke-dasharray="${dash.toFixed(2)} ${gap.toFixed(2)}" stroke-dashoffset="${(-offset).toFixed(2)}" transform="rotate(-90 ${cx} ${cy})" />`;
      offset += dash;
      return markup;
    })
    .join('');
  return `<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg">${arcs}<circle cx="${cx}" cy="${cy}" r="${r - strokeWidth / 2 - 1}" fill="white" /></svg>`;
}
